// ============================================================
// CART DRAWER — sidebar laterale del carrello
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../../contexts/CartContext';
import { useAuth } from '../../../../contexts/AuthContext';
import CartItemComponent from '../cartItem/CartItem';
import LoginModal from '../../loginModal/LoginModal';
import './CartDrawer.css';

const CartDrawer: React.FC = () => {
  const { items, isOpen, closeCart, clearCart, totalPrice, totalItems } =
    useCart();
  const { isAuthenticated } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const pendingCheckout = useRef(false);
  const navigate = useNavigate();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Chiudi con ESC
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) closeCart();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closeCart]);

  // Blocca scroll body quando il drawer è aperto
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Quando il login va a buon fine con checkout pendente → naviga
  useEffect(() => {
    if (isAuthenticated && pendingCheckout.current) {
      pendingCheckout.current = false;
      closeCart();
      navigate('/checkout');
    }
  }, [isAuthenticated, closeCart, navigate]);

  // ── Checkout handler ──────────────────────────────────────
  const handleCheckout = () => {
    if (!isAuthenticated) {
      pendingCheckout.current = true;
      setShowLoginModal(true);
    } else {
      closeCart();
      navigate('/checkout');
    }
  };

  return (
    <>
      {/* Login Modal — aperto sul posto prima di andare al checkout */}
      <LoginModal
        show={showLoginModal}
        onHide={() => {
          setShowLoginModal(false);
          pendingCheckout.current = false;
        }}
        onLoginSuccess={() => setShowLoginModal(false)}
        disableRedirect
      />

      {/* Overlay */}
      <div
        className={`cart-overlay ${isOpen ? 'cart-overlay--visible' : ''}`}
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`cart-drawer ${isOpen ? 'cart-drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Carrello acquisti"
      >
        {/* Header */}
        <div className="cart-drawer-header">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-cart3 fs-5" />
            <h2 className="cart-drawer-title">
              Il tuo carrello
              {totalItems > 0 && (
                <span className="cart-drawer-count">{totalItems}</span>
              )}
            </h2>
          </div>
          <button
            className="cart-drawer-close"
            onClick={closeCart}
            aria-label="Chiudi carrello"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="cart-drawer-body">
          {items.length === 0 ? (
            <div className="cart-empty">
              <i className="bi bi-cart-x cart-empty-icon" />
              <p className="cart-empty-title">Il carrello è vuoto</p>
              <p className="cart-empty-sub">
                Esplora i corsi e aggiungi quello che fa per te.
              </p>
              <button
                className="cart-empty-cta"
                onClick={() => {
                  closeCart();
                  navigate('/rimettiti-al-centro');
                }}
              >
                Scopri i corsi
              </button>
            </div>
          ) : (
            <div className="cart-items-list">
              {items.map((item) => (
                <CartItemComponent key={item.course.id} item={item} />
              ))}
            </div>
          )}
        </div>

        {/* Footer con totale e CTA */}
        {items.length > 0 && (
          <div className="cart-drawer-footer">
            {/* Totale */}
            <div className="cart-total">
              <span className="cart-total-label">Totale</span>
              <span className="cart-total-price">
                € {totalPrice.toFixed(2)}
              </span>
            </div>

            {/* Nota sicurezza */}
            <p className="cart-security-note">
              <i className="bi bi-shield-lock me-1" />
              Pagamento sicuro — i tuoi dati sono protetti
            </p>

            {/* CTA Checkout */}
            <button className="cart-checkout-btn" onClick={handleCheckout}>
              {isAuthenticated ? (
                <>
                  <i className="bi bi-credit-card me-2" />
                  Procedi al pagamento
                </>
              ) : (
                <>
                  <i className="bi bi-lock me-2" />
                  Accedi e paga
                </>
              )}
            </button>

            {/* Svuota carrello */}
            <button className="cart-clear-btn" onClick={clearCart}>
              <i className="bi bi-trash me-1" />
              Svuota carrello
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
